import { Avatar, Drawer, IconButton, InputAdornment, Skeleton, TextField, Typography } from "@mui/material";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import "../tradegpt.scss";
import ListItemIcon from "@mui/material/ListItemIcon";
import React, { useState, useMemo, useEffect, lazy, Suspense } from "react";
import { RootState, SidebarProps } from "../../assets/interfaces/interfaces";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useDispatch, useSelector } from "react-redux";
import { clearUser, requestSignOut } from "../auth/authActions";
import SignInModal from "../auth/SignInModal";
import SettingsModal from "../SettingsModal";
import { AppDispatch } from "../../redux/store";
import { fetchPastSession } from "../../redux/slices/pastSessionSlice";
import { useLocation, useParams } from "react-router-dom";
import ContactSupportModal from "../ContactModal";
import {
  clearFinchatData,
  finchatMessageCancel,
  finchatMessageCancelAll,
} from "../../redux/slices/finchatSlice";
import { findLongestName } from "../../helpers";
import classNames from "classnames";
import {
  fetchActivePlan,
  resetPlanStatus,
} from "../../redux/slices/activePlanSlice";
import { getAuth, getIdToken } from "firebase/auth";
// import Cookies from "universal-cookie";
import SidebarPromoModal from "../SidebarPromoModal";
import useEnhancedNavigate, {
  extractParams,
  urlIsAppScheme,
} from "../Session/helpers";
import { fetchToken } from "../../redux/slices/fetchAuthToken";
import { fetchUser, openAuthModal } from "../../redux/slices/userSlice";
import { resetValidUserState } from "../../redux/slices/validateUserSlice";
import { resetState } from "../../redux/slices/deleteUserSlice";
import { hidePromo } from "../../redux/slices/IntroPromoSlice";
import { NewWindowIcon } from "../Plans/icons/GPTIcons/NewWindowIcon";
import { logClickEvent } from "../../analytics";
import ChatList from "./ChatList";
import { ReactComponent as LogoFilledFull } from "../Icons/LogoFilledFull.svg";
import { ReactComponent as NewChat } from "../Icons/NewChat.svg";
import { LeaderboardRounded, PendingActionsRounded, SearchRounded, ThumbUpAltRounded } from "@mui/icons-material";
import SidebarPromoItem from "./PromoBannerItem";
import { usePaginatedChats } from "./hooks";
import { AuthActions } from "./constants";
import { useMessages } from "../../context/MessagesContext";
import { v4 as uuidv4 } from "uuid";
import { setShowSearchModal } from "../../redux/slices/searchSlice";

const RecommendedPromptsModal = lazy(() =>
  import("../RecommendedPromptsModal"),
);
const PlanModal = lazy(() =>
  import("../Plans/PlanModal"),
);

const PreferencesModal = lazy(() =>
  import("./PreferencesModal"),
);
const Sidebar: React.FC<SidebarProps> = ({
  isMobile,
  drawerOpen,
  setDrawerOpen,
}) => {
  const [source, setSource] = useState(""); // State to store the extracted parameters
  const navigate = useEnhancedNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { session_id } = useParams();
  const [anchorEl, setAnchorEl] = useState(null);
  const [nestedAnchor, setNestedAnchor] = useState(null);
  const [userAvatar, setUserAvatar] = useState("");
  const [showPromo, setShowPromo] = useState(false);
  const [recommendedPromptsModalOpened, setRecommendedPromptsModalOpened] = useState(false);
  const [isPreferencesModalOpen, setIsPreferencesModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [initialActiveMenu, setInitialActiveMenu] = useState(null);

  const tokenStatus = useSelector((state: { token }) => state.token.status);
  const user = useSelector((state: RootState) => state.auth.currentUser);
  const authLoading = useSelector((state: RootState) => state.auth.loading);
  const showIntroPromo = useSelector(
    (state: { introPromo }) => state.introPromo.show,
  );
  const finchatLoading = useSelector(
    (state: RootState) => state.finchat.loading,
  );
  const userPreferences = useSelector(
    (state: { userPreferences }) => state.userPreferences,
  );
  const auth = getAuth();
  const isUserSignedIn = useMemo(() => {
    return !!auth?.currentUser;
  }, [auth?.currentUser]);

  const plan = useSelector((state: { plan }) => state.plan.plan);

  useEffect(() => {
    const paramsSource = extractParams(window.location.search);
    setSource(paramsSource);

    const settings = localStorage.getItem("postLoginSettings");
    if (settings === "notifications") {
      setInitialActiveMenu(2);
      setIsSettingsModalOpen(true); // triggers modal open after mount
      localStorage.removeItem("postLoginSettings");
    }
  }, []);

  const { data, isLoading, fetchNextPage, hasNextPage, isFetching } = usePaginatedChats({ userId: user?.userId });
  const { setMessages } = useMessages();

  const clearAllChatData = () => {
    setMessages((allMessages) => {
      return {
        ...allMessages,
        [session_id]: [],
      };
    });
    handleProfileMenuClose();
    dispatch(clearFinchatData());
    handleNewChat();
  };

  const handleCancel = () => {
    dispatch(finchatMessageCancel(session_id));
  };

  const handleNewChat = () => {
    handleCancel();

    const newSessionId = uuidv4();
    navigate(`/chat/${newSessionId}`);

    setMessages(allMessages => ({
      ...allMessages,
      [newSessionId]: [],
    }));
  };

  const handleSignOut = () => {
    dispatch(resetState());
    dispatch(resetValidUserState());
    dispatch(requestSignOut());
    dispatch(clearUser());
    dispatch(resetPlanStatus());
    // Lets force to cancel a message if there is any loading
    dispatch(finchatMessageCancelAll());
    clearAllChatData();
  };

  useEffect(() => {
    if (!isUserSignedIn || !user?.email)
      // return setUserAvatar("https://gravatar.com/avatar/?s=200&d=mp");
      if (user?.photoURL) return setUserAvatar(user.photoURL);

    const encoder = new TextEncoder();
    const data = encoder.encode(user?.email);
    crypto.subtle.digest("SHA-256", data).then((hash) => {
      const hashString = Array.from(new Uint8Array(hash))
        .map(b => b.toString(16).padStart(2, "0"))
        .join("");
      setUserAvatar(
        `https://www.gravatar.com/avatar/${hashString}?s=200&d=identicon`,
      );
    });
  }, [user?.email, user?.photoURL, isUserSignedIn]);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSupportMenuOpen = (event) => {
    setNestedAnchor(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
    setNestedAnchor(null);
  };

  const handleSupportClose = () => {
    setNestedAnchor(null);
    setAnchorEl(null);
  };

  const toggleSettingsModal = () => {
    setIsSettingsModalOpen(!isSettingsModalOpen);
  };

  useEffect(() => {
    if (tokenStatus === "succeeded" && session_id) {
      dispatch(fetchPastSession({ sessionId: session_id }));
    }
  }, [session_id, dispatch, tokenStatus]);

  const [planModalOpen, setPlanModalOpen] = useState(false);
  const authStatus = useSelector((state: { user }) => state.user.status);
  const activePlanStatus = useSelector((state: { plan }) => state.plan.status);
  const currentUser = auth.currentUser;
  useEffect(() => {
    if (
      currentUser
      && !user
      && (authStatus === "failed" || activePlanStatus === "failed")
      && authStatus !== "loading"
      && activePlanStatus !== "loading"
    ) {
      // fetch token
      const redirect = window.location.search.split("redirect_uri=")[1] ?? "/";
      const isMobileApp = urlIsAppScheme(redirect);
      if (tokenStatus === "idle") {
        getIdToken(auth.currentUser, true).then((token) => {
          dispatch(
            fetchToken({
              token,
              isMobileApp, // If it was an in-app browser call, we will get the token on the response.
            }),
          );
        });
      }
      else {
        if (tokenStatus === "succeeded") {
          dispatch(fetchUser({ source }));
          dispatch(fetchActivePlan());
        }
      }
    }
  }, [currentUser, user, authStatus, activePlanStatus, tokenStatus, source]);

  const longestName = useMemo(
    () => findLongestName(currentUser, user),
    [currentUser, user],
  );

  useEffect(() => {
    if (!isLoading) {
      const timer = setTimeout(() => {
        setShowPromo(true);
      }, 2000);

      return () => clearTimeout(timer);
    }
    else {
      setShowPromo(false);
    }
  }, [isLoading]);

  return (
    <div
      className={classNames("md:flex-shrink-0 overflow-x-hidden z-50", {
        hidden: !drawerOpen,
      })}
    >
      {recommendedPromptsModalOpened && (
        <Suspense fallback={null}>
          <RecommendedPromptsModal
            open={recommendedPromptsModalOpened}
            handleClose={() => setRecommendedPromptsModalOpened(false)}
            onLimitReached={() => setPlanModalOpen(true)}
          />
        </Suspense>
      )}
      <SignInModal />
      {planModalOpen && (
        <Suspense fallback={null}>
          <PlanModal
            open={planModalOpen}
            handleClose={() => {
              setPlanModalOpen(false);
              handleProfileMenuClose();
            }}
          />
        </Suspense>
      )}
      {showIntroPromo && (
        <SidebarPromoModal
          handleClose={() => {
            dispatch(hidePromo());
          }}
          open={showIntroPromo}
        />
      )}

      <SettingsModal
        clearAllChatData={clearAllChatData}
        handleProfileMenuClose={handleProfileMenuClose}
        open={isSettingsModalOpen}
        setOpen={setIsSettingsModalOpen}
        handleSignOut={handleSignOut}
        initialActiveMenu={initialActiveMenu}
      />

      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={drawerOpen}
        onClose={() => setDrawerOpen(!drawerOpen)}
        anchor="left"
        className="w-[260px]"
        PaperProps={{
          className:
            "h-screen bg-white dark:bg-background/paper-elevation-2 w-[260px] !opacity-100",
        }}
      >
        <div className="flex-1  min-h-0 overflow-hidden">
          <div className="flex-1 h-full flex flex-col gap-y-4 transition-opacity opacity-100 p-4 border-r border-mui-black-12">
            <section>
              <button
                onClick={() => {
                  logClickEvent("new_session_click");
                  handleNewChat();
                }}
                disabled={finchatLoading}
                className="w-full flex items-center justify-between cursor-pointer min-h-10 ps-2"
              >
                <div className="flex items-center gap-x-1">
                  <LogoFilledFull className="w-6 h-6 text-black dark:text-white" />
                  <div className="text-mui-black-87 dark:text-white text-sm font-semibold">
                    TradeGPT
                  </div>
                </div>
                <IconButton>
                  <NewChat className="h-6 w-6 text-black dark:text-white" />
                </IconButton>
              </button>
            </section>

            <section>
              <TextField
                onClick={() => {
                  if (!isLoading) {
                    dispatch(setShowSearchModal(true));
                    logClickEvent("search_chats_click");
                  }
                }}
                type="text"
                placeholder="Search"
                className="search-input !bg-components-input-filled-enabledFill dark:!bg-transparent"
                variant="outlined"
                fullWidth
                autoComplete="off"
                disabled={!currentUser}
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRounded className="w-6 h-6 !fill-action-active" />
                    </InputAdornment>
                  ),
                  sx: {
                    "& input": {
                      padding: "12px",
                    },
                  },
                }}
              />
            </section>

            <section className="flex flex-col gap-y-2 text-mui-black-87 dark:text-white tracking-[0.17px]">
              <div
                className={classNames("px-2 py-[6px] flex gap-x-[12px] cursor-pointer items-center rounded-[4px] hover:bg-text-states-hover",
                  { "bg-tgpt-secondary-states-selected": location.pathname === "/dashboard" },
                )}
                onClick={() => navigate("/dashboard")}
              >
                <LeaderboardRounded className="text-action-active w-6 h-6" />
                <Typography variant="body2/regular-400">Dashboard</Typography>
              </div>
              <div
                className={classNames("px-2 py-[6px] flex gap-x-[12px] cursor-pointer items-center rounded-[4px] hover:bg-text-states-hover",
                  { "bg-tgpt-secondary-states-selected": location.pathname === "/tasks" },
                )}
                onClick={() => navigate("/tasks")}
              >
                <PendingActionsRounded className="text-action-active w-6 h-6" />
                <Typography variant="body2/regular-400">Tasks</Typography>
              </div>
              <div
                className="px-2 py-[6px] flex gap-x-[12px] cursor-pointer items-center rounded-[4px] hover:bg-text-states-hover"
                onClick={() => {
                  setRecommendedPromptsModalOpened(true);
                }}
              >
                <ThumbUpAltRounded className="text-action-active w-6 h-6" />
                <Typography variant="body2/regular-400">Recommended Prompts</Typography>
              </div>
            </section>

            <section className="flex-1 flex-col transition-opacity duration-500 overflow-x-hidden overflow-y-auto custom-scrollbar box-content me-[-4px] pe-[4px]">
              <div className="">
                {authLoading
                  ? (
                      <>
                        <Typography variant="overline/regular-400" className="ps-2 tracking-[1px] dark:text-mui-white-70">LOADING...</Typography>
                        <div className="flex flex-col pt-2 gap-y-2">
                          {Array.from({ length: 10 }).map((_, index) => (
                            <Skeleton key={index} variant="rectangular" height={36} width={228} className="!bg-tgpt-secondary-accentLight" />
                          ))}

                        </div>
                      </>
                    )
                  : (
                      !!isUserSignedIn
                      && (
                        <ChatList
                          data={data}
                          fetchNextPage={fetchNextPage}
                          hasNextPage={hasNextPage}
                          isFetching={isFetching}
                          clearAllChatData={clearAllChatData}
                        />
                      )
                    )}
              </div>
            </section>

            <section className="flex flex-col">
              <div className="h-[98px] relative overflow-hidden">
                <div
                  className={`absolute inset-0 transition-all duration-700 ease-out transform ${
                    showPromo ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                  }`}
                >
                  <SidebarPromoItem />
                </div>
              </div>
            </section>

            <section
              className={classNames(
                "flex items-center py-[6px] px-2 rounded-[4px] w-full",
                {
                  "bg-tgpt-secondary-accentLight animate-pulse h-10": authLoading,
                  "hover:bg-tgpt-secondary-states-hover": !authLoading && !anchorEl,
                  "bg-tgpt-secondary-states-selected": !authLoading && anchorEl,
                },
              )}
            >
              {!authLoading && (
                <button
                  className="flex w-full items-center gap-x-2"
                  disabled={authLoading}
                  onClick={event =>
                    isUserSignedIn
                      ? handleProfileMenuOpen(event)
                      : dispatch(openAuthModal({ type: AuthActions.SIGNIN }))}
                  type="button"
                >
                  <div className="relative flex">
                    <Avatar
                      alt="User"
                      className="!w-8 !h-8"
                      src={userAvatar}
                    />
                    {userPreferences.hasOpenedModal === false && (
                      <div className="absolute top-0 right-0 h-2 w-2 rounded-full bg-warning-main"></div>
                    )}
                  </div>
                  <div className="max-w-[176px] truncate">
                    <Typography
                      variant="body1/regular-400"
                      className="text-token-text-primary"
                    >
                      {isUserSignedIn ? longestName : "Sign In"}
                    </Typography>
                  </div>
                </button>
              )}
            </section>
          </div>
        </div>

        <Menu
          className="menulist-home body1"
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleProfileMenuClose}
          anchorOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          slotProps={{
            paper: {
              style: {
                transform: "translateY(-4%)",
                marginTop: "-10px",
              },
            },
          }}
        >
          <MenuItem
            className="submenu-item"
            onClick={() => {
              logClickEvent("my_plan_click");
              setPlanModalOpen(true);
            }}
          >
            <div>My Plan</div>
            {plan?.subscription_type !== "Premium" && (
              <div className="w-[75px] rounded-[100px] text-[13px] text-white bg-primary-blue-main text-center px-[3px]">
                Upgrade
              </div>
            )}
          </MenuItem>

          <MenuItem onClick={handleSupportMenuOpen} className="submenu-item">
            <div>Support</div>
            <ListItemIcon className="ps-4">
              <ChevronRightIcon className="right-arrow text-mui-black-87 dark:text-white" />
            </ListItemIcon>
          </MenuItem>

          {isPreferencesModalOpen && (
            <Suspense fallback={null}>
              <PreferencesModal
                open={isPreferencesModalOpen}
                handleClose={() => {
                  setIsPreferencesModalOpen(false);
                  handleProfileMenuClose();
                }}
              />
            </Suspense>
          )}

          <MenuItem
            className="submenu-item"
            onClick={() => {
              logClickEvent("preference_click");
              setIsPreferencesModalOpen(true);
            }}
          >
            <div>Preferences</div>
            {userPreferences.hasOpenedModal === false && (
              <div className="rounded-full bg-warning-main h-2 w-2"></div>
            )}
          </MenuItem>

          <MenuItem
            className="submenu-item"
            onClick={() => {
              logClickEvent("setting_click");
              toggleSettingsModal();
            }}
          >
            <div>Settings</div>
          </MenuItem>
          <MenuItem
            className="submenu-item"
            onClick={() => {
              logClickEvent("logout_click");
              handleSignOut();
            }}
          >
            <div>Log Out</div>
          </MenuItem>
        </Menu>
        {/* Support nested menu */}
        <Menu
          className="nested-menulist-home"
          anchorEl={nestedAnchor}
          open={Boolean(nestedAnchor)}
          onClose={handleSupportClose}
          anchorOrigin={{
            vertical: "center",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "center",
            horizontal: "left",
          }}
        >
          <ContactSupportModal
            open={isContactModalOpen}
            handleClose={() => {
              setIsContactModalOpen(false);
              handleProfileMenuClose();
            }}
          />
          <MenuItem
            className="submenu-item"
            onClick={() => {
              logClickEvent("contact_support_click");
              setIsContactModalOpen(true);
            }}
          >
            <div className="">Contact Support</div>
          </MenuItem>
          <MenuItem
            className="submenu-item"
            onClick={() => {
              logClickEvent("faq&help_click");
              navigate("/faq");
            }}
          >
            FAQ & Help Center
            <NewWindowIcon className="dark:fill-white fill-black" />
          </MenuItem>
          <MenuItem
            className="submenu-item"
            onClick={() => {
              logClickEvent("terms&policy_click");
              navigate("/policies");
            }}
          >
            Terms and Policies
            <NewWindowIcon className="dark:fill-white fill-black" />
          </MenuItem>
          <MenuItem
            className="nested-submenu-item"
            onClick={() => navigate("/release-notes")}
          >
            Release Notes
          </MenuItem>
        </Menu>
      </Drawer>
    </div>
  );
};

export default Sidebar;
